(() => {
  if (window.BTProductPageInitialized) return;
  window.BTProductPageInitialized = true;

  const ROOT_SELECTOR = 'product-info.bt-product-page';

  const visiblePriceText = (root) => {
    const priceRoot = root.querySelector(`[id^='price-']`);
    if (!priceRoot) return '';

    const salePrice = priceRoot.querySelector('.price__sale .price-item--sale');
    const regularPrice = priceRoot.querySelector('.price__regular .price-item--regular');
    const fallbackPrice = priceRoot.querySelector('.price-item--regular, .price-item--sale');
    return (salePrice || regularPrice || fallbackPrice)?.textContent?.trim() || '';
  };

  const setupAccordions = (root) => {
    const details = Array.from(root.querySelectorAll('.product__info-container .product__accordion details'));

    details.forEach((item) => {
      if (item.dataset.btAccordionReady === 'true') return;
      item.dataset.btAccordionReady = 'true';

      item.addEventListener('toggle', () => {
        if (!item.open) return;
        details.forEach((otherItem) => {
          if (otherItem !== item) otherItem.removeAttribute('open');
        });
      });
    });
  };

  const setupBundleOffers = (root) => {
    const offerRoot = root.querySelector('.smitam-bundle-offers');
    if (!offerRoot || offerRoot.dataset.btBundleReady === 'true') return;

    const offers = Array.from(offerRoot.querySelectorAll('[data-smitam-offer]'));
    const quantityInput = root.querySelector('.product-form__quantity .quantity__input');
    const thresholds = [2, 3, 20];
    const offerScopes = ['10% off entire order', '15% off entire order', '20% off entire order'];
    if (!offers.length || !quantityInput) return;

    offerRoot.dataset.btBundleReady = 'true';

    const selectedOfferForQuantity = (quantity) => {
      let selectedOffer = null;
      offers.forEach((offer, index) => {
        if (quantity >= thresholds[index]) selectedOffer = offer;
      });
      return selectedOffer;
    };

    const syncSelection = () => {
      const quantity = Math.max(0, Number.parseInt(quantityInput.value || '0', 10) || 0);
      const selectedOffer = selectedOfferForQuantity(quantity);

      offers.forEach((offer) => {
        const isSelected = offer === selectedOffer;
        offer.classList.toggle('is-featured', isSelected);
        offer.setAttribute('aria-pressed', String(isSelected));
      });
    };

    offers.forEach((offer, index) => {
      const threshold = thresholds[index];
      offer.dataset.minQuantity = String(threshold);

      const offerMeta = offer.querySelector('.smitam-bundle-offers__offer-meta');
      const scopeText = Array.from(offerMeta?.childNodes || []).find((node) => node.nodeType === Node.TEXT_NODE);
      if (scopeText) scopeText.textContent = `${offerScopes[index]} `;

      offer.addEventListener('click', () => {
        const minimum = Math.max(1, Number.parseInt(quantityInput.dataset.min || quantityInput.min || '1', 10) || 1);
        const maximum = Number.parseInt(quantityInput.dataset.max || quantityInput.max || '', 10);
        let targetQuantity = Math.max(minimum, threshold);

        if (Number.isFinite(maximum)) targetQuantity = Math.min(targetQuantity, maximum);

        quantityInput.value = String(targetQuantity);
        quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        syncSelection();
      });
    });

    quantityInput.addEventListener('input', syncSelection);
    quantityInput.addEventListener('change', syncSelection);
    syncSelection();
  };

  const setupStickyCart = (root) => {
    const sticky = root.querySelector('[data-bt-sticky-cart]');
    if (!sticky || sticky.dataset.btStickyReady === 'true') return;
    sticky.dataset.btStickyReady = 'true';

    const sectionId = sticky.dataset.sectionId;
    const stickyButton = sticky.querySelector('[data-bt-sticky-submit]');
    const stickyLabel = sticky.querySelector('[data-bt-sticky-label]');
    const stickyPrice = sticky.querySelector('[data-bt-sticky-price]');
    const stickyVariant = sticky.querySelector('[data-bt-sticky-variant]');
    const stickyImage = sticky.querySelector('.bt-product__sticky-image');
    const stickySpinner = stickyButton?.querySelector('.loading__spinner');
    const saleBadge = root.querySelector('[data-bt-sale-badge]');
    const saleValue = root.querySelector('[data-bt-sale-value]');

    const mainButton = () =>
      root.querySelector(`#ProductSubmitButton-${CSS.escape(sectionId)}`) ||
      root.querySelector('.product-form__submit[type="submit"]');

    const syncButton = () => {
      const sourceButton = mainButton();
      if (!sourceButton || !stickyButton) return;

      const isLoading = sourceButton.classList.contains('loading');
      stickyButton.disabled =
        sourceButton.disabled || sourceButton.getAttribute('aria-disabled') === 'true' || isLoading;
      stickyButton.classList.toggle('loading', isLoading);
      stickyButton.setAttribute('aria-busy', String(isLoading));
      stickySpinner?.classList.toggle('hidden', !isLoading);
      const sourceLabel = sourceButton.querySelector('span')?.textContent?.trim();
      const currentPrice = visiblePriceText(root);
      if (currentPrice && stickyPrice) stickyPrice.textContent = currentPrice;
      if (sourceLabel && stickyLabel) {
        const includePriceInLabel = window.matchMedia('(max-width: 749px)').matches;
        stickyLabel.textContent =
          stickyButton.disabled || !currentPrice || !includePriceInLabel
            ? sourceLabel
            : `${sourceLabel} \u00b7 ${currentPrice}`;
      }
    };

    const syncVariant = (variant) => {
      if (!variant) {
        if (stickyButton) stickyButton.disabled = true;
        if (saleBadge) saleBadge.classList.add('hidden');
        return;
      }

      if (stickyVariant) {
        const title = variant.title && variant.title !== 'Default Title' ? variant.title : '';
        stickyVariant.textContent = title;
        stickyVariant.hidden = title === '';
      }

      if (stickyImage && variant.featured_media?.preview_image?.src) {
        stickyImage.src = variant.featured_media.preview_image.src;
        stickyImage.removeAttribute('srcset');
      }

      const compareAtPrice = Number(variant.compare_at_price || 0);
      const price = Number(variant.price || 0);
      const discount = compareAtPrice > price ? Math.round(((compareAtPrice - price) * 100) / compareAtPrice) : 0;

      if (saleBadge) saleBadge.classList.toggle('hidden', discount === 0);
      if (saleValue) saleValue.textContent = String(discount);
      syncButton();
    };

    let ticking = false;
    const updateVisibility = () => {
      ticking = false;
      const sourceButton = mainButton();
      if (!sourceButton) return;

      const hasPassedButton = sourceButton.getBoundingClientRect().bottom < 0;
      const shouldShow = hasPassedButton;
      sticky.classList.toggle('is-visible', shouldShow);
      sticky.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
      if (stickyButton) {
        if (shouldShow) {
          stickyButton.removeAttribute('tabindex');
        } else {
          stickyButton.setAttribute('tabindex', '-1');
        }
      }
    };

    const requestVisibilityUpdate = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(updateVisibility);
    };

    stickyButton?.addEventListener('click', () => {
      const sourceButton = mainButton();
      if (
        !sourceButton ||
        sourceButton.disabled ||
        sourceButton.getAttribute('aria-disabled') === 'true' ||
        sourceButton.classList.contains('loading')
      ) {
        return;
      }

      const sourceForm = sourceButton.closest('form');
      if (sourceForm?.requestSubmit) {
        sourceForm.requestSubmit(sourceButton);
      } else {
        sourceButton.click();
      }
    });

    let sourceButtonObserver;
    let observedSourceButton;
    const observeSourceButton = () => {
      const sourceButton = mainButton();
      if (!sourceButton || sourceButton === observedSourceButton) return;

      sourceButtonObserver?.disconnect();
      observedSourceButton = sourceButton;
      sourceButtonObserver = new MutationObserver(syncButton);
      sourceButtonObserver.observe(sourceButton, {
        attributes: true,
        childList: true,
        subtree: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class'],
      });
      syncButton();
    };

    observeSourceButton();

    const priceRoot = root.querySelector(`[id^='price-']`);
    if (priceRoot) {
      new MutationObserver(syncButton).observe(priceRoot, { childList: true, subtree: true });
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (String(data.sectionId) !== String(sectionId)) return;
        observeSourceButton();
        syncVariant(data.variant);
      });
    }

    window.addEventListener('scroll', requestVisibilityUpdate, { passive: true });
    window.addEventListener('resize', requestVisibilityUpdate, { passive: true });
    window.addEventListener('resize', syncButton, { passive: true });
    syncButton();
    updateVisibility();
  };

  const initRoot = (root) => {
    if (!(root instanceof HTMLElement)) return;
    setupAccordions(root);
    setupBundleOffers(root);
    setupStickyCart(root);
  };

  const initAll = (scope = document) => {
    if (scope.matches?.(ROOT_SELECTOR)) initRoot(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initRoot);
  };

  initAll();

  document.addEventListener('product-info:loaded', (event) => initRoot(event.target));
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
